import React from 'react'

import classnames from 'classnames'

import SyncedTranscript from './SyncedTranscript'
import Slider from '../../common/components/AccessibleSlider'
import { PlaylistItem, formatTimeMS, PlayerStatus, Collection } from 'mytours-read-common'
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner, Text, Grid, GridRow } from '@mytours/components'
import Modal from '../../common/components/modal'

import { formatPlayPauseLabel } from '../functions'
import { calculatePercentage, seekToPercentage, DEFAULT_SKIP_SECONDS } from '../soundController'
import { getDirMode, getStyleProperty, isRTL } from '../../platform'
import { EventActionsEnum, sendEvent } from '../../logging/analytics'
import strings from '../../../strings'

import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../../common_lib/utils'
import { ionHistory } from 'mobile_web/modules/navigation'
import { updateTranscriptionHash } from 'mobile_web/modules/screen/functions'

import './styles/style.less'
import { MediaItem } from 'mytours-read-api-typescript'
import { getImageAlt } from 'mobile_web/modules/common/functions'
import { isIOSDevice } from 'mobile_web/modules/platform/device'

interface Props {
	currentAudio: PlaylistItem
	onPlayPause: () => void
	onBack: () => void
	onForward: () => void
	collection: Collection
}

interface State {
	progress: number // slider progress % out of 100
	userDraggingSlider: boolean
	showTranscriptModal: boolean
	coverImageItem: MediaItem
	maxImageHeight: number
}

/**
 * The initial state for our internal component state.
 */
const INITIAL_STATE: State = {
	progress: 0,
	userDraggingSlider: false,
	showTranscriptModal: false,
	coverImageItem: null,
	maxImageHeight: 0,
}

export default class FullScreenAudioPlayer extends React.Component<Props, State> {
	state = INITIAL_STATE
	_imageContainer: HTMLDivElement
	private hash = ''
	private unListen: () => void

	constructor(props: Props) {
		super(props)

		const progress: number = calculatePercentage(props.currentAudio.currentTime, props.currentAudio.duration) || 0
		this.state = { ...INITIAL_STATE, progress }
		this._imageContainer = null
	}

	componentDidMount = () => {
		this.setupCoverImage()

		addEventListener('resize', this.updateMaxImageSize)

		this.unListen = ionHistory.listen(({ hash }) => {
			if (hash !== this.hash) {
				// TODO: this is temp fix: add global handle for hash changes
				if (hash === '' && this.hash === '#transcript' && this.state.showTranscriptModal) {
					this.toggleTranscriptModal(false)
				}

				this.hash = hash
			}
		})
	}

	componentDidUpdate = (prevProps: Props) => {
		this.onCurrentTimeUpdate(prevProps.currentAudio.currentTime)
		this.updateMaxImageSize()
	}

	componentWillUnmount = () => {
		removeEventListener('resize', this.updateMaxImageSize)

		this.unListen()
	}

	private updateMaxImageSize = (): void => {
		if (!this._imageContainer) return

		const { clientHeight } = this._imageContainer
		const { maxImageHeight } = this.state

		// We only use `maxImageHeight` to control the image display. It could be a boolean flag, really.
		if (clientHeight > 0 && maxImageHeight !== clientHeight) {
			this.setState({ maxImageHeight: clientHeight }, this.setupCoverImage)
		}
	}

	private onCurrentTimeUpdate = (prevCurrentTime: number): void => {
		const {
			currentAudio: { currentTime, duration },
		} = this.props
		if (prevCurrentTime !== currentTime) {
			const { progress, userDraggingSlider } = this.state
			const updatedProgress = userDraggingSlider ? progress : calculatePercentage(currentTime, duration)

			this.setState({ progress: updatedProgress })

			this.setupCoverImage()
		}
	}

	private setupCoverImage = (): void => {
		const {
			currentAudio: { currentTime, audioTrack },
		} = this.props
		const { coverImages: dataCoverImages = [], startTimes: dataStartTimes = [], audio } = audioTrack || {}

		const startTimes = {}

		const coverImagesReverse = [...dataCoverImages]
		const startTimesReverse = [...dataStartTimes]

		let thumbnailMediaItem = coverImagesReverse.length > 0 ? undefined : audio.thumbnail_media_item

		for (let i = 0; i < dataStartTimes.length; i++) {
			if (coverImagesReverse[i]) {
				// {i + 1} - skip first item, because it's audio file with time `0`. we don't need if
				startTimes[startTimesReverse[i + 1]] = coverImagesReverse[i]
			}
		}

		for (const time of Object.keys(startTimes)) {
			if (currentTime > parseFloat(time) && startTimes?.[time]) {
				thumbnailMediaItem = startTimes[time]
			}
		}

		this.setState({ coverImageItem: thumbnailMediaItem })
	}

	openTranscript = (): void => {
		const { currentAudio } = this.props
		const { audioTrack } = currentAudio || {}

		if (audioTrack?.audio.transcription) {
			this.toggleTranscriptModal(true)
		}
	}

	onSliderValueChanged = (value: number): void => {
		if (!this.state.userDraggingSlider) {
			this.setState({ userDraggingSlider: true })
		}
		this.setState({ progress: value })
	}

	onSlidingComplete = (): void => {
		const { currentAudio } = this.props
		const { duration, audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}
		let { progress } = this.state
		progress = Math.round(progress)

		seekToPercentage(audio?.id, progress, duration, container, audio?.file?.id)

		this.setState({ userDraggingSlider: false })
	}

	getBackButton = (): JSX.Element => (
		<Button
			variant="clear"
			className="time-controls-btn"
			onClick={this.props.onBack}
			aria-label={
				strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
			}
		>
			<i
				className="fak fa-lg fa-skip-back-10"
				aria-label={
					strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
				}
			/>
		</Button>
	)

	getForwardButton = (): JSX.Element => (
		<Button
			variant="clear"
			className="time-controls-btn"
			onClick={this.props.onForward}
			aria-label={strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string}
		>
			<i
				className="fak fa-lg fa-skip-forward-10"
				aria-label={
					strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string
				}
			/>
		</Button>
	)

	getPlayPauseButton = (): JSX.Element => {
		const { currentAudio, onPlayPause } = this.props
		const { playerStatus, audioTrack } = currentAudio || {}
		const isPlaying = playerStatus === PlayerStatus.Playing
		const title = audioTrack?.audio?.title

		return (
			<Button
				variant="clear"
				onClick={onPlayPause}
				aria-label={formatPlayPauseLabel(isPlaying, title)}
				// `ariaLabel` won't change unless we remount
				key={formatPlayPauseLabel(isPlaying, title)}
			>
				<i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
			</Button>
		)
	}

	getTranscriptButton = (): JSX.Element => (
		<Button aria-label={strings.transcript} variant="clear" onClick={this.openTranscript}>
			<i className="far fa-align-left" />
			<Text variant="inherit">{strings.transcript}</Text>
		</Button>
	)

	toggleTranscriptModal = (value?: boolean): void => {
		this.setState((prev: State) => {
			updateTranscriptionHash(value, prev.showTranscriptModal, () => {
				const { currentAudio, collection } = this.props
				const { audioTrack } = currentAudio || {}

				sendEvent(EventActionsEnum.AudioViewTranscript, {
					audioTitle: audioTrack.audio?.name,
					collection: collection?.name,
					collectionId: collection?.id,
				})
			})

			return {
				showTranscriptModal: typeof value === 'boolean' ? value : !prev.showTranscriptModal,
			}
		})
	}

	renderTranscriptModal = (): JSX.Element => {
		const {
			currentAudio: { audioTrack, currentTime, playerStatus },
		} = this.props
		const { showTranscriptModal } = this.state
		const audio = audioTrack?.audio

		return (
			<Modal isOpen={showTranscriptModal} onDidDismiss={() => this.toggleTranscriptModal(false)}>
				<Card variant="ios" className="synced-transcript-modal-card" dir={getDirMode()}>
					<CardHeader>
						<CardTitle as="h2" id="dialogTitle">
							{strings.transcript}
						</CardTitle>
						<Button
							variant="clear"
							className="close"
							onClick={() => this.toggleTranscriptModal(false)}
							aria-label={strings.close}
						>
							<i className="fal fa-xmark" />
						</Button>
					</CardHeader>
					<CardContent>
						<SyncedTranscript
							variant="modal"
							transcription={audio?.transcription}
							transcriptionFormat={audio?.transcription_format}
							mediaItemId={audio?.id}
							currentTime={currentTime}
							isPlaying={playerStatus === PlayerStatus.Playing}
						/>
					</CardContent>
				</Card>
			</Modal>
		)
	}

	onImageRef = (ref: HTMLDivElement) => {
		setTimeout(() => {
			this._imageContainer = ref
			this.updateMaxImageSize()
		}, 0)
	}

	render() {
		const {
			currentAudio: { playerStatus, currentTime, duration, audioTrack },
		} = this.props
		const { progress, coverImageItem, maxImageHeight } = this.state

		const isLoaded = playerStatus === PlayerStatus.Playing || playerStatus === PlayerStatus.Pause

		// format time
		const elapsedTime: number = currentTime ? Math.round(currentTime) : 0
		const formattedDuration = duration ? Math.round(duration) : 0
		const remainingTime: number = duration && currentTime ? Math.round(duration - currentTime) : formattedDuration
		const formattedElapsedTime: string = formatTimeMS(elapsedTime)
		const formattedRemainingTime: string = '-' + formatTimeMS(remainingTime)

		const margin = Number(getStyleProperty('--content-margin-horizontal')) || 0

		// transcript
		const hasTranscript = Boolean(audioTrack?.audio?.transcription)

		const coverImage =
			coverImageItem?.file &&
			generateImageUrl(coverImageItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			})
		const coverImageSrcSet =
			coverImageItem?.file &&
			generateImageSrcSet(coverImageItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			})

		return (
			<Grid className="audio-player audio-player-full">
				{coverImage && (
					<GridRow ref={this.onImageRef} className="image-row" style={{ opacity: maxImageHeight ? 1 : 0 }}>
						<div className="image-wrap">
							<img src={coverImage} srcSet={coverImageSrcSet} className="image" alt={getImageAlt(coverImageItem)} />
						</div>
					</GridRow>
				)}

				{coverImageItem?.caption && audioTrack.slideshowEnabled ? (
					<GridRow className="image-caption">
						<div dangerouslySetInnerHTML={{ __html: coverImageItem.caption }} />
					</GridRow>
				) : null}

				<GridRow className="player-row" style={{ marginLeft: margin, marginRight: margin }}>
					<GridRow className={classnames('title-row', !coverImage && 'no-cover-image')}>
						<Text variant="inherit">{audioTrack?.audio?.title}</Text>
					</GridRow>
					<GridRow>
						<GridRow className="time-row" dir="ltr">
							<Text variant="inherit">{formattedElapsedTime}</Text>
							<Text variant="inherit">{formattedRemainingTime}</Text>
						</GridRow>
						<GridRow className="audio-range-row" dir="ltr">
							<Slider
								ariaLabel={strings.accessibility_audio_seek}
								className={`slider-range ${isIOSDevice() && 'slider-range-ios'}`}
								disabled={!isLoaded}
								max={100}
								min={0}
								value={progress}
								tooltip={false}
								onChange={this.onSliderValueChanged}
								onChangeComplete={this.onSlidingComplete}
							/>
						</GridRow>
					</GridRow>
					<GridRow className={`players-controls ${isRTL() ? 'rtl' : ''}`}>
						{/* Back 10 seconds */}
						{this.getBackButton()}

						{/* Play/Pause */}
						{playerStatus === PlayerStatus.Loading ? (
							<Spinner variant="crescent" aria-label={strings.loading} />
						) : (
							this.getPlayPauseButton()
						)}

						{/* Forward 10 seconds */}
						{this.getForwardButton()}
					</GridRow>
					<GridRow className="transcript-row">{hasTranscript && this.getTranscriptButton()}</GridRow>

					{hasTranscript && this.renderTranscriptModal()}
				</GridRow>
			</Grid>
		)
	}
}
